import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { useUrlTableSort } from "@/components/ui/sortable-table";
import { api } from "@/lib/api";
import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import { formatHours } from "@/lib/utils";
import { Link, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

/**
 * `/projects/:slug/time-entries` — the project's time-entry list,
 * rendered inside the project layout. Same column set as the
 * global `/time-entries` page but scoped to one project (the
 * "Project" column is dropped since every row would match).
 * Approve / Reject are inline, shown only when the viewer has the
 * project `approve_time` permission.
 */

const SORT_KEYS = [
  "spentOn",
  "user",
  "workItem",
  "hours",
  "source",
  "approval",
  "billing",
] as const;
type SortKey = (typeof SORT_KEYS)[number];

const searchSchema = z.object({
  q: z.string().optional(),
  ...sortSearchFields(SORT_KEYS),
  ...paginationSearchFields,
});

type TimeEntry = {
  id: string;
  spentOn: string;
  hours: string | number;
  source: string;
  approvalState: string;
  billingState: string;
  user: { id: string; name: string | null } | null;
  externalUserEmail?: string | null;
  workItem: { id: string; title: string; globalId: number } | null;
};

type TimeEntriesResponse = {
  timeEntries: TimeEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalHours: number;
  canApprove: boolean;
};

const approvalVariant: Record<
  string,
  "default" | "secondary" | "success" | "outline" | "destructive"
> = {
  worked: "secondary",
  approved: "success",
  paid: "outline",
  rejected: "destructive",
};

const billingVariant: Record<string, "default" | "secondary" | "success" | "warning" | "outline"> =
  {
    unbilled: "secondary",
    to_invoice: "warning",
    invoiced: "success",
    paid: "success",
    non_billable: "outline",
  };

// `paid` means different things per axis — spell them out (employee
// paid out vs customer invoice paid) to avoid the "two paids" mix-up.
const approvalLabel: Record<string, string> = {
  worked: "worked",
  budget: "budget",
  approved: "approved",
  paid: "paid out",
  rejected: "rejected",
};
const billingLabel: Record<string, string> = {
  unbilled: "unbilled",
  to_invoice: "to invoice",
  invoiced: "invoiced",
  paid: "invoice paid",
  non_billable: "non-billable",
};

export const Route = createFileRoute("/_app/projects/$slug/time-entries")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    sort: search.sort,
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: ({ params, deps }) => {
    const qs = new URLSearchParams();
    if (deps.q) qs.set("q", deps.q);
    if (deps.sort) qs.set("sort", deps.sort);
    if (deps.dir) qs.set("dir", deps.dir);
    if (deps.page) qs.set("page", String(deps.page));
    if (deps.pageSize) qs.set("pageSize", String(deps.pageSize));
    const suffix = qs.toString();
    return api.get<TimeEntriesResponse>(
      `/api/projects/${params.slug}/time-entries${suffix ? `?${suffix}` : ""}`,
    );
  },
  component: ProjectTimeEntriesPage,
});

function ProjectTimeEntriesPage() {
  const { timeEntries, total, page, pageSize, totalHours, canApprove } = Route.useLoaderData();
  const search = Route.useSearch();
  const params = Route.useParams();
  const router = useRouter();
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);

  const sort = useUrlTableSort({
    sortKey: search.sort ?? null,
    sortDir: search.dir ?? "desc",
    onChange: (key, dir) =>
      navigate({
        to: "/projects/$slug/time-entries",
        params: { slug: params.slug },
        search: { ...search, sort: key as SortKey, dir, page: undefined },
      }),
  });

  async function decide(timeEntryId: string, state: "approved" | "rejected") {
    setPending(timeEntryId);
    try {
      await api.post(`/api/projects/${params.slug}/time-entries/approve`, {
        timeEntryId,
        state,
      });
      await router.invalidate();
    } finally {
      setPending(null);
    }
  }

  const columns: Array<DataTableColumn<TimeEntry>> = [
    {
      key: "spentOn",
      header: "Date",
      sortAccessor: (te) => te.spentOn,
      cellClassName: "text-sm text-muted-foreground",
      cell: (te) => <LocalDate value={te.spentOn} />,
    },
    {
      key: "user",
      header: "User",
      sortAccessor: (te) => te.user?.name ?? te.externalUserEmail ?? null,
      cellClassName: "font-medium",
      cell: (te) => te.user?.name ?? te.externalUserEmail ?? "External user",
    },
    {
      key: "workItem",
      header: "Work Item",
      sortAccessor: (te) => te.workItem?.title ?? null,
      cellClassName: "text-sm max-w-[280px] truncate",
      cell: (te) =>
        te.workItem ? (
          <Link
            to="/work-item/$id"
            params={{ id: String(te.workItem.globalId) }}
            className="text-primary hover:underline"
          >
            {te.workItem.title}
          </Link>
        ) : (
          <span className="italic text-muted-foreground">Project overhead</span>
        ),
    },
    {
      key: "hours",
      header: "Hours",
      sortAccessor: (te) => Number(te.hours),
      cell: (te) => formatHours(Number(te.hours)),
    },
    {
      key: "source",
      header: "Source",
      sortAccessor: (te) => te.source,
      cell: (te) => (
        <Badge variant="outline" className="text-xs">
          {te.source}
        </Badge>
      ),
    },
    {
      key: "approval",
      header: "Approval",
      sortAccessor: (te) => te.approvalState,
      cell: (te) => (
        <Badge variant={approvalVariant[te.approvalState] ?? "outline"}>
          {approvalLabel[te.approvalState] ?? te.approvalState}
        </Badge>
      ),
    },
    {
      key: "billing",
      header: "Billing",
      sortAccessor: (te) => te.billingState,
      cell: (te) => (
        <Badge variant={billingVariant[te.billingState] ?? "outline"}>
          {billingLabel[te.billingState] ?? te.billingState}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      hidden: !canApprove,
      cell: (te) =>
        te.approvalState === "worked" ? (
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={pending === te.id}
              onClick={() => decide(te.id, "approved")}
            >
              Approve
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="h-7 text-xs"
              disabled={pending === te.id}
              onClick={() => decide(te.id, "rejected")}
            >
              Reject
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold">Time Entries</h2>
          <p className="text-muted-foreground text-sm mt-1">
            {total} {total === 1 ? "entry" : "entries"} · {formatHours(totalHours)} total
          </p>
        </div>
        <Button asChild>
          <Link to="/time-entries/new" search={{ project: params.slug }}>
            New Entry
          </Link>
        </Button>
      </div>

      <SearchInput placeholder="Search by work item, user or description..." />

      <DataTable
        columns={columns}
        rows={timeEntries}
        rowKey={(te) => te.id}
        sort={sort}
        emptyState="No time entries yet."
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="project-time-entries"
        basePath={`/projects/${params.slug}/time-entries`}
        searchParams={{
          q: search.q,
          sort: search.sort,
          dir: search.dir,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
